import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Label,
  Switch,
  Textarea,
} from '@cargomatic/ui';
import Alert from 'components/ui/Alert';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { evaluateBillingStatusByReferenceNumbers } from 'utils/ShipmentWebAPIUtils';
import useModal from 'hooks/useModal';
import { ConfirmationModal } from 'components/v2/confirmation-modal';

export default function BulkEvaluateBillingStatus() {
  const [shipmentIdsText, setShipmentIdsText] = useState('');
  const [forcePauseBillStatusEvaluation, setForcePauseBillStatusEvaluation] =
    useState(false);

  const [errors, setErrors] = useState({});

  const hasErrors = useMemo(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  const {
    visible: evaluateBillingStatusConfirmationModalVisible,
    show: showEvaluateBillingStatusConfirmationModal,
    close: closeEvaluateBillingStatusConfirmationModal,
  } = useModal();

  function validate() {
    const errors = {};

    if (shipmentIds.length === 0) {
      errors.shipmentIds = `${t('SHIPMENT_IDS')} ${t('REQUIRED')}`;
    }
    setErrors(errors);
    return errors;
  }

  const { t } = useTranslation();

  const shipmentIds = useMemo(() => {
    if (!shipmentIdsText) {
      return [];
    }
    const items = shipmentIdsText.split('\n').reduce((list, line) => {
      return list.concat(
        line
          .split(',')
          .map((item) => item.trim())
          .filter((item) => !!item),
      );
    }, []);

    return items;
  }, [shipmentIdsText]);

  function handleShipmentIdsTextChange(e) {
    setShipmentIdsText(e.target.value);
  }

  function handleShowConfirmationModal() {
    const errors = validate();

    if (Object.keys(errors).length > 0) {
      return;
    }
    showEvaluateBillingStatusConfirmationModal();
  }
  async function handleSave() {
    const errors = validate();

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      await evaluateBillingStatusByReferenceNumbers({
        shipmentReferences: shipmentIds,
        forcePauseBillStatusEvaluation,
      });
      showToast(`Billing Status evaluated!`);
    } catch (e) {
      showToast(`There was a problem processing your request.`);
      console.error(e);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk Reevaluate Billing Status</CardTitle>
        <CardDescription>
          Paste in the reference numbers to reevaluate the billing status.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {forcePauseBillStatusEvaluation && (
          <Alert type="warning" className="my-4">
            Any previously paused items will be unpaused and re-evaluated.
          </Alert>
        )}
        {hasErrors && (
          <Alert type="error" className="my-4">
            Please fill out all required fields.
          </Alert>
        )}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="force-evaluations"
              checked={forcePauseBillStatusEvaluation}
              onCheckedChange={setForcePauseBillStatusEvaluation}
            />
            <Label htmlFor="pause-automated-evaluations">Force Evaluate</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="shipmentIds">Shipment Reference Numbers</Label>
            <Textarea
              value={shipmentIdsText}
              id="shipmentIds"
              onChange={handleShipmentIdsTextChange}
            />
          </div>
        </div>
        {evaluateBillingStatusConfirmationModalVisible && (
          <ConfirmationModal
            open={evaluateBillingStatusConfirmationModalVisible}
            onHide={closeEvaluateBillingStatusConfirmationModal}
            title={t('EVALUATE_BILLING_STATUS')}
            description={t('EVALUATE_BILLING_STATUS_CONFIRMATION', {
              count: shipmentIds.length,
            })}
            onConfirm={async () => {
              await handleSave();
            }}
          />
        )}
      </CardContent>
      <CardFooter>
        <Button onClick={handleShowConfirmationModal}>Save</Button>
      </CardFooter>
    </Card>
  );
}
